import { get, set } from '@ember/object';
import Validator from 'validator';

/**
 * @param {Object} param
 * @param {string} param.min_rate
 * @param {string} param.max_rate
 * @returns {string|undefined}
 */
export const minRateValidator = ({ min_rate, max_rate }) => {
  if (!Validator.isInt(min_rate)) {
    return 'Min rate must be a valid number';
  }

  const minRateNumber = parseInt(min_rate, 10);

  if (minRateNumber < 10) {
    return 'Min rate must be at least 10';
  }

  if (Validator.isInt(max_rate)) {
    const maxRateNumber = parseInt(max_rate, 10);

    if (maxRateNumber < minRateNumber) {
      return 'Min rate must be less than max rate';
    }
  }
};

/**
 * @param {Object} param
 * @param {string} param.min_rate
 * @param {string} param.max_rate
 * @returns {string|undefined}
 */
export const maxRateValidator = ({ min_rate, max_rate }) => {
  if (!Validator.isInt(max_rate)) {
    return 'Max rate must be a valid number';
  }

  const maxRateNumber = parseInt(max_rate, 10);

  if (maxRateNumber < 10) {
    return 'Max rate must be at least 10';
  }

  if (Validator.isInt(min_rate)) {
    const minRateNumber = parseInt(min_rate, 10);

    if (maxRateNumber < minRateNumber) {
      return 'Max rate must be greater than min rate';
    }
  }
};

const slugRegex = /^[a-zA-Z][\-_a-zA-Z0-9]*$/;

/**
 * @param {string} slug
 * @returns {string|undefined}
 */
export const slugValidator = (slug) => {
  if (!slug) return 'Display name is a required field';
  if (slug.length < 5) return 'Display name must be at least 5 characters';
  if (!slugRegex.test(slug)) return 'Display name must begin with a letter, and can only contain letters (a-z, A-Z), numbers, dashes, and underscores';
};

const phoneRegex = /^.{7,}$/;

/**
 * Ideally we should just use Validator.isMobilePhone() here, but we are using an old
 * version of the library where you have to specify a locale. Newer versions will validate
 * against all locales if you don't specify. For now, just use the regex the server uses
 * to validate
 *
 * @param {string} phone
 * @returns {string|undefined}
 */
export const phoneValidator = (phone) => {
  if (!phoneRegex.test(phone)) return 'Please enter your full phone number including country code (e.g. +1)';
};

// keep this list in sync with backend DISALLOWED_WORK_SAMPLE_URLS
const DISALLOWED_FILE_SHARING_URLS = [
  '.box.',
  'docs.google.co',
  'drive.google.co',
  'dropbox.com',
  'icloud.com',
  'mediafire.com',
  'mega.nz',
  'onedrive.live.com',
  '.pcloud.',
  'sharepoint.com',
  'wetransfer.com',
  'we.tl',
];

/**
 * @param {string} url
 * @returns {string|undefined}
 */
export const workSampleUrlValidator = (url) => {
  if (!url) return;
  const testUrl = url.toLowerCase();
  const usesDisallowedUrl = DISALLOWED_FILE_SHARING_URLS.find((urlFragment) => {
    return testUrl.includes(urlFragment);
  });

  // disallow any url related to file sharing service
  if (usesDisallowedUrl) {
    return "Sorry! We can't accept URLs from file sharing websites.";
  }
};

/**
 * @param {string} name
 * @returns {string|undefined}
 */
export const fullNameValidator = (name) => {
  const parts = name.trim().split(/\s+/);

  if (parts.length < 2) {
    return 'Please provide both first and last names.';
  }

  if (!parts.every(value => /[A-Za-zÀ-ÖØ-öø-ÿ-_.]+/.test(value))) {
    return 'Your name contains invalid characters.';
  }
};

/**
 * @param {string} email
 * @returns {string|undefined}
 */
export const emailValidator = (email) => {
  if (!Validator.isEmail(email)) return 'Please provide a valid email address.';
};

/**
 * @param {string} left
 * @param {string} right
 * @returns {string|undefined}
 */
 export const confirmEmailValidator = (left, right) => {
  if (!right) return 'You must confirm your email';
  if (left !== right) return 'Your emails must match.';
};

/**
 * @param {string} password
 * @returns {string|undefined}
 */
export const passwordValidator = (password) => {
  const errorMessage = 'Minimum 8 characters, at least 1 uppercase, 1 lowercase, 1 symbol, and 1 number (0-9) required.';

  if (!password) return errorMessage;
  if (password.length < 8) return errorMessage;
  if (password.length >= 14) return; // Drop the special requirements for passwords >= 14 characters
  if (!/[0-9]/.test(password)) return errorMessage;
  if (!/[!"\\#$%&'()*+,-.\/:;<=>?@[\]^_`{|}~]/.test(password)) return errorMessage;
  if (!/[a-z]/.test(password)) return errorMessage;
  if (!/[A-Z]/.test(password)) return errorMessage;
};

/**
 * @param {string} left
 * @param {string} right
 * @returns {string|undefined}
 */
export const confirmPasswordValidator = (left, right) => {
  if (!right) return 'You must confirm your password';
  if (left !== right) return 'Your passwords must match.';
};

const protocols = ['http', 'https'];

/**
 * @param {string} website
 * @return {string|undefined}
 */
export const websiteValidator = (website) => {
  if (!Validator.isURL(website, { protocols })) return 'You must enter a valid url';
};

export const validateInputs = (scope, validators, callback) => {
  const errors = { };

  const hasErrors = Object.entries(validators).reduce((hasErrors, [field, validator]) => {
    const value = get(scope, field);
    const error = validator(value);

    if (error) {
      errors[field] = error;
      return true;
    }

    return hasErrors;
  }, false);

  set(scope, 'errors', errors);

  if (!hasErrors) {
    callback.call(scope);
  }
};

export const validateSchema = (object, schema) => {
  try {
    return [schema.validateSync(object, { abortEarly: false })];
  } catch (e) {
    const errors = e.inner.reduce((accum, inner) => {
      accum[inner.path] = inner.errors[0];
      return accum;
    }, { })

    return [null, errors];
  }
};
